// ── useWhatsAppRealtime ────────────────────────────────────────────
//
// Mounted ONCE per logged-in session (in DashboardLayout) so realtime
// notifications work on every page, not just the inbox. Idempotent —
// safe to call from additional components without doubling subscriptions
// (the underlying singleton is mounted once per tab).
//
// Responsibilities:
//   • Connect the realtime singleton with the current JWT (and
//     reconnect when the token rotates after a refresh).
//   • Trigger a browser notification on `wa:message:new` when the
//     vendor's preference is on AND the OS permission is granted AND
//     the document is hidden (don't double-notify on the active tab).
//   • Play a soft ping when sound alerts are enabled.
//   • Expose nothing — this hook is a side-effect-only mount point.
//     Components that want to react to specific events should call
//     `realtime.subscribe(...)` directly with their own cleanup.

import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { realtime, RealtimeEnvelope } from '@/lib/realtime';
import {
  getRealtimePrefs,
  getBrowserNotificationPermission,
  onPrefsChange,
  playInboundPing,
} from '../realtime/notification-prefs';

/**
 * Mount the WhatsApp realtime listener for the current session.
 * Returns nothing; cleanup happens automatically on unmount.
 */
export function useWhatsAppRealtime(): void {
  const { user, isAuthenticated } = useAuth();
  // Track the last token we connected with so we only reconnect on rotation.
  const lastTokenRef = useRef<string | null>(null);
  // Cache prefs in a ref so the message handler reads the current values
  // without re-subscribing on every change.
  const prefsRef = useRef(getRealtimePrefs());

  // ── (1) Connection lifecycle ──────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      // Logged out → tear down. No reconnect attempts while signed out.
      realtime.disconnect();
      lastTokenRef.current = null;
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    if (lastTokenRef.current !== token) {
      lastTokenRef.current = token;
      realtime.connect(token);
    }
  }, [isAuthenticated, user?.id]);

  // ── (2) Watch for token rotation (refresh-token flow) ─────────
  // The axios interceptor rotates `accessToken` in localStorage without
  // notifying anyone. Poll once a minute as a cheap safety net — if the
  // stored token changed, reconnect with the new one. Costs ~zero.
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = window.setInterval(() => {
      const current = localStorage.getItem('accessToken');
      if (current && current !== lastTokenRef.current) {
        lastTokenRef.current = current;
        realtime.disconnect();
        realtime.connect(current);
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, [isAuthenticated]);

  // ── (3) Keep the prefs ref live ───────────────────────────────
  useEffect(() => {
    return onPrefsChange(() => {
      prefsRef.current = getRealtimePrefs();
    });
  }, []);

  // ── (4) Wildcard subscription for side effects ────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    const off = realtime.subscribeAll((evt: RealtimeEnvelope) => {
      switch (evt.type) {
        case 'wa:message:new': {
          handleInboundMessage(evt, prefsRef.current);
          break;
        }
        // Other events flow through silently; component hooks subscribe
        // to them via realtime.subscribe(...) for cache updates.
        default:
          break;
      }
    });
    return off;
  }, [isAuthenticated]);
}

// ── Helpers ────────────────────────────────────────────────────────

function handleInboundMessage(
  evt: RealtimeEnvelope,
  prefs: { browserNotifications: boolean; soundAlerts: boolean },
): void {
  const data = (evt.data ?? {}) as {
    conversationId?: string;
    contactWaId?: string;
    messageType?: string;
  };

  // Sound: always plays (cheap, useful) when enabled. No OS permission
  // needed beyond the autoplay policy (handled inside playInboundPing).
  if (prefs.soundAlerts) {
    playInboundPing();
  }

  // Notification: only when the tab is hidden — don't pop a system
  // banner for a message the agent is literally already looking at.
  if (
    prefs.browserNotifications &&
    typeof document !== 'undefined' &&
    document.visibilityState === 'hidden' &&
    getBrowserNotificationPermission() === 'granted'
  ) {
    try {
       
      const notifOptions: any = {
        body: `${data.messageType ?? 'message'} from ${data.contactWaId ?? 'a contact'}`,
        // Tag identical to conversationId so repeated messages on the
        // same thread collapse into one notification instead of stacking.
        tag: `wa:conv:${data.conversationId ?? 'unknown'}`,
        // renotify: true surfaces the latest message even if same tag is
        // already showing. Not in all TS lib targets; typed as `any` above.
        renotify: true,
      };
      const n = new Notification('New WhatsApp message', notifOptions);
      n.onclick = () => {
        try {
          window.focus();
          if (data.conversationId) {
            // Navigate to the inbox with the conversation pre-selected.
            window.location.href = `/whatsapp/inbox?c=${data.conversationId}`;
          }
        } catch {
          /* swallow */
        }
        n.close();
      };
    } catch {
      /* Notification constructor can throw on older Safari */
    }
  }
}
